import React, { useState, useContext } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { User, Lock, AlertCircle, Smartphone } from 'lucide-react';
import api from '../utils/api';
import WelcomeBoss from '../components/WelcomeBoss';
import { isBossLikeRole, normalizeRole } from '../utils/roleUtils';
import toast from 'react-hot-toast';

const Login = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(false);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showWelcomeBoss, setShowWelcomeBoss] = useState(false);
    const [loggedInUser, setLoggedInUser] = useState(null);

    const { login, user, loading, isAuthenticated } = useContext(AuthContext);
    const navigate = useNavigate();
    const normalizedRole = normalizeRole(user?.role);

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            const response = await api.post('/auth/login', { username, password, rememberMe });
            const { user, token } = response.data;

            login(user, token, rememberMe);

            if (isBossLikeRole(user.role)) {
                setLoggedInUser(user);
                setShowWelcomeBoss(true);
            } else if (user.role === 'shipment_staff') {
                toast.success(`Hoşgeldin ${user.username}!`);
                navigate('/shipments');
            } else {
                toast.success(`Hoşgeldin ${user.username}!`);
                navigate('/dashboard');
            }

        } catch (err) {
            if (err.response?.data?.message) {
                setError(err.response.data.message);
            } else if (err.request) {
                setError('Sunucuya ulasildi ancak yanit alinamadi. Lutfen sayfayi yenileyip tekrar deneyin.');
            } else {
                setError('Giris istegi baslatilamadi.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleWelcomeComplete = () => {
        navigate('/dashboard');
    };

    if (showWelcomeBoss && loggedInUser) {
        return (
            <WelcomeBoss
                username={loggedInUser.username}
                onComplete={handleWelcomeComplete}
            />
        );
    }

    if (!loading && isAuthenticated && user) {
        return <Navigate to={normalizedRole === 'shipment_staff' ? '/shipments' : '/dashboard'} replace />;
    }

    return (
        <div className="relative min-h-screen overflow-hidden flex items-center justify-center p-4">

            {/* Animasyonlu Arka Plan */}
            <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-primary-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
            <div className="absolute top-[-10%] right-[-10%] w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>

            {/* Login Kartı */}
            <div className="w-full max-w-md glass rounded-2xl p-8 shadow-2xl relative z-10 border border-slate-700/50">
                <div className="text-center mb-10">
                    <div className="flex items-center justify-center mb-6 bg-white/95 rounded-xl p-4 shadow-lg w-full max-w-[200px] mx-auto">
                        <img src="/logo.png" alt="Öz Yürek Tekstil" className="w-full h-auto object-contain" />
                    </div>
                    <h2 className="text-2xl font-bold tracking-tight text-dark-text mb-2">Öz Yürek Tekstil</h2>
                    <p className="text-dark-text/70 text-sm">Finans & Tahsilat Yönetimi</p>
                </div>

                <form onSubmit={handleLogin} className="space-y-6">
                    {/* Hata Mesajı */}
                    {error && (
                        <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 text-red-500 font-medium rounded-xl text-sm transition-all shadow-inner">
                            <AlertCircle size={20} className="shrink-0" />
                            <p>{error}</p>
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-dark-text mb-2">Kullanıcı Adı</label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-dark-text/50">
                                <User size={20} />
                            </div>
                            <input
                                type="text"
                                className="block w-full pl-12 pr-4 py-3 bg-dark-card border border-dark-text/20 rounded-xl text-dark-text placeholder-dark-text/40 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all font-medium"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                required
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-dark-text mb-2">Şifre</label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-dark-text/50">
                                <Lock size={20} />
                            </div>
                            <input
                                type="password"
                                className="block w-full pl-12 pr-4 py-3 bg-dark-card border border-dark-text/20 rounded-xl text-dark-text placeholder-dark-text/40 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all font-medium"
                                placeholder="••••••••"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                            />
                        </div>
                    </div>

                    {/* BENİ HATIRLA */}
                    <div className="flex items-center gap-3">
                        <input
                            type="checkbox"
                            id="rememberMe"
                            checked={rememberMe}
                            onChange={(e) => setRememberMe(e.target.checked)}
                            className="w-4 h-4 rounded border-dark-text/30 bg-dark-card text-primary-500 focus:ring-primary-500 focus:ring-offset-0 cursor-pointer accent-primary-500"
                        />
                        <label htmlFor="rememberMe" className="text-sm text-dark-text/70 cursor-pointer select-none">
                            Beni Hatırla <span className="text-dark-text/40">(30 gün oturum)</span>
                        </label>
                    </div>

                    <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full flex justify-center items-center py-3.5 px-4 border border-transparent rounded-xl shadow-lg text-sm font-bold text-white bg-primary-600 hover:bg-primary-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 ease-in-out transform hover:-translate-y-0.5 active:translate-y-0"
                    >
                        {isLoading ? (
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        ) : (
                            'Giriş Yap'
                        )}
                    </button>
                </form>

                {/* Mobil Uygulama İndirme */}
                <div className="mt-6 text-center">
                    <a
                        href="/api/download/app"
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/20 hover:border-emerald-500/30 transition-all duration-200 text-sm font-medium group"
                    >
                        <Smartphone size={16} className="group-hover:scale-110 transition-transform" />
                        Android Uygulamayı İndir
                    </a>
                </div>

                <div className="mt-6 text-center border-t border-dark-text/10 pt-6">
                    <p className="text-xs text-dark-text/60 flex items-center justify-center gap-1.5 font-medium">
                        Yetkili sistem. <Lock size={12} className="text-dark-text/50" /> Açık üyelik kısıtlanmıştır.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Login;
